import { HttpError, paginateLocally } from "@nasam/kernel";

import {
  POI_ICONS,
  type Poi,
  type PoiBulkRow,
  type PoisRepository,
} from "../domain";

export interface MockPoisRepositoryOptions {
  readonly pois?: readonly Poi[];
  /** Simulated network latency, so the saving state shows in the app. */
  readonly latencyMs?: number;
}

/** The API's search: part of the name, any case. */
function matching(pois: readonly Poi[], search: string | undefined): Poi[] {
  const needle = search?.trim().toLocaleLowerCase() ?? "";
  return pois.filter((poi) => poi.name.toLocaleLowerCase().includes(needle));
}

function notFound(id: string, method: "GET" | "PATCH" | "DELETE"): HttpError {
  return new HttpError("POI not found.", {
    status: 404,
    code: undefined,
    method,
    url: `/pois/${id}`,
    data: {
      statusCode: 404,
      path: `/api/v1/pois/${id}`,
      message: "POI not found.",
    },
  });
}

function conflict(): HttpError {
  return new HttpError("POI already exists.", {
    status: 409,
    code: undefined,
    method: "POST",
    url: "/pois",
    data: {
      statusCode: 409,
      path: "/api/v1/pois",
      message: "POI already exists.",
    },
  });
}

function mockPoi(
  id: string,
  name: string,
  latitude: number,
  longitude: number,
  icon: Poi["icon"],
  remarks: string | null = null,
): Poi {
  return {
    id,
    name,
    latitude,
    longitude,
    icon,
    remarks,
    accountId: "account-1",
    createdAt: "2026-10-01T08:00:00.000Z",
    updatedAt: "2026-10-01T08:00:00.000Z",
  };
}

/** The POI screen's sample data: spots around Riyadh, where the map opens. */
export const MOCK_POIS: readonly Poi[] = [
  mockPoi(
    "poi-1",
    "المستودع الرئيسي - شمال الرياض",
    24.84861,
    46.66219,
    "COMPANY",
  ),
  mockPoi(
    "poi-2",
    "King Fahd Medical City",
    24.68924,
    46.70432,
    "HOSPITAL",
    "Emergency entrance on the north side.",
  ),
  mockPoi("poi-3", "Al Yasmin School", 24.82493, 46.64087, "SCHOOL"),
  mockPoi(
    "poi-4",
    "Fuel station - Exit 9",
    24.77251,
    46.73884,
    "GAS",
    "Open 24 hours.",
  ),
  mockPoi(
    "poi-5",
    "Manhole - King Abdullah Rd",
    24.73418,
    46.68852,
    "CRITICAL",
  ),
  mockPoi("poi-6", "Traffic sign - Olaya St", 24.70117, 46.68457, "TRAFFIC"),
  mockPoi("poi-7", "Fire hydrant - Al Malqa", 24.80512, 46.61576, "MAJOR"),
  mockPoi("poi-8", "Street light - Al Nakheel", 24.75645, 46.62703, "MINOR"),
  mockPoi("poi-9", "Driver rest house", 24.65729, 46.71812, "HOUSE"),
  mockPoi("poi-10", "Visitor info point", 24.63801, 46.71294, "INFO"),
];

type MockSheetRow = Omit<PoiBulkRow, "status" | "errors">;

function sheetRow(
  row: number,
  name: string | null,
  latitude: number | string | null,
  longitude: number | string | null,
  icon: string | null,
  remarks: string | null = null,
): MockSheetRow {
  return { row, name, latitude, longitude, icon, remarks };
}

/** What the mock reads from any uploaded sheet (it can't open a real
 *  workbook): valid rows, invalid ones, a name repeated in the file and one
 *  MOCK_POIS already holds. */
export const MOCK_BULK_SHEET: readonly MockSheetRow[] = [
  sheetRow(2, "Central warehouse", 24.7136, 46.6753, "COMPANY"),
  sheetRow(3, "Al Noor Hospital", 24.6907, 46.6851, "HOSPITAL", "Gate 3."),
  sheetRow(4, null, 24.7012, 46.6624, "SCHOOL"),
  sheetRow(5, "Fuel station - Exit 5", 95, 46.7, "GAS"),
  sheetRow(6, "Riyadh Park", 24.7556, 46.6297, "Airport"),
  sheetRow(7, "Al Olaya library", 24.6948, 46.6851, "INFO"),
  sheetRow(8, "central warehouse", 24.7136, 46.6753, "COMPANY"),
  sheetRow(9, "Al Yasmin School", 24.82493, 46.64087, "SCHOOL"),
  sheetRow(10, "Rest house - Exit 7", 24.7811, 46.7322, "HOUSE"),
  sheetRow(11, "Speed bump - Takhassusi", 24.7303, 46.6577, "Trafic"),
  sheetRow(12, "Water tank - Al Malqa", 24.8102, "east", "MINOR"),
  sheetRow(13, "Checkpoint - Northern Ring", 24.8019, 46.6905, "MAJOR"),
];

/** The icon a sheet cell names (any case, `Trafic` too), if supported. */
function sheetIcon(value: string | null): Poi["icon"] | undefined {
  const upper = value?.trim().toUpperCase();
  const icon = upper === "TRAFIC" ? "TRAFFIC" : upper;
  return POI_ICONS.find((supported) => supported === icon);
}

function sheetCoordinate(value: number | string | null, max: number) {
  const number = typeof value === "number" ? value : Number.NaN;
  return Math.abs(number) <= max ? number : undefined;
}

/** The API's checks, row by row: required, in-range values first, then
 *  duplicates -- of an earlier valid row, or of a POI already saved. */
function checkSheet(
  sheet: readonly MockSheetRow[],
  pois: readonly Poi[],
): PoiBulkRow[] {
  const saved = new Set(pois.map((poi) => poi.name.toLocaleLowerCase()));
  const firstRows = new Map<string, number>();
  return sheet.map((row) => {
    const errors: string[] = [];
    const name = row.name?.trim() ?? "";
    if (name === "") errors.push("POI name is required.");
    if (sheetCoordinate(row.latitude, 90) === undefined)
      errors.push("Latitude must be a number between -90 and 90.");
    if (sheetCoordinate(row.longitude, 180) === undefined)
      errors.push("Longitude must be a number between -180 and 180.");
    if (sheetIcon(row.icon) === undefined)
      errors.push("Invalid POI icon. Please select a supported icon.");
    if (errors.length > 0) return { ...row, status: "INVALID", errors };
    const key = name.toLocaleLowerCase();
    if (saved.has(key)) {
      return {
        ...row,
        status: "DUPLICATE",
        errors: [`POI "${name}" already exists.`],
      };
    }
    const first = firstRows.get(key);
    if (first !== undefined) {
      return {
        ...row,
        status: "DUPLICATE",
        errors: [
          `POI "${name}" appears more than once in the file (first on row ${String(first)}).`,
        ],
      };
    }
    firstRows.set(key, row.row);
    return { ...row, status: "VALID", errors: [] };
  });
}

function unsupportedFile(file: File): HttpError | undefined {
  if (file.name.toLocaleLowerCase().endsWith(".xlsx")) return undefined;
  const message =
    "Unsupported file format. Please upload an Excel file using the provided template.";
  return new HttpError(message, {
    status: 415,
    code: undefined,
    method: "POST",
    url: "/pois/bulk-upload/preview",
    data: { statusCode: 415, path: "/api/v1/pois/bulk-upload/preview", message },
  });
}

/** POIs kept in memory, with the API's one rule a form can trip over: a
 *  name is unique within the company, regardless of case. */
export function createMockPoisRepository(
  options: MockPoisRepositoryOptions = {},
): PoisRepository {
  const pois: Poi[] = [...(options.pois ?? [])];
  const latency = options.latencyMs ?? 0;
  const wait = async () => {
    if (latency > 0)
      await new Promise((resolve) => setTimeout(resolve, latency));
  };
  let next = pois.length;
  return {
    async list(query) {
      await wait();
      // Newest first, as the screen asks the API for.
      const rows = matching(pois, query.search).reverse();
      return paginateLocally(rows, query.page, query.limit);
    },
    async listForMap(query) {
      await wait();
      return matching(pois, query.search).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    },
    async create(input) {
      await wait();
      const name = input.name.trim();
      if (
        pois.some(
          (poi) => poi.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
        )
      ) {
        throw conflict();
      }
      next += 1;
      const now = new Date().toISOString();
      const remarks = input.remarks?.trim();
      const poi: Poi = {
        id: `poi-${String(next)}`,
        name,
        latitude: input.latitude,
        longitude: input.longitude,
        icon: input.icon,
        remarks: remarks === undefined || remarks === "" ? null : remarks,
        accountId: "account-1",
        createdAt: now,
        updatedAt: now,
      };
      pois.push(poi);
      return { poi, message: "POI created successfully." };
    },
    async update(id, input) {
      await wait();
      const index = pois.findIndex((poi) => poi.id === id);
      const current = pois[index];
      if (current === undefined) throw notFound(id, "PATCH");
      const name = input.name?.trim();
      if (
        name !== undefined &&
        pois.some(
          (poi) =>
            poi.id !== id &&
            poi.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
        )
      ) {
        throw conflict();
      }
      const remarks =
        input.remarks === undefined
          ? current.remarks
          : input.remarks === null || input.remarks.trim() === ""
            ? null
            : input.remarks.trim();
      const poi: Poi = {
        ...current,
        ...(name === undefined ? {} : { name }),
        ...(input.latitude === undefined ? {} : { latitude: input.latitude }),
        ...(input.longitude === undefined
          ? {}
          : { longitude: input.longitude }),
        ...(input.icon === undefined ? {} : { icon: input.icon }),
        remarks,
        updatedAt: new Date().toISOString(),
      };
      pois[index] = poi;
      return { poi, message: "POI updated successfully." };
    },
    async exportPois(query) {
      await wait();
      // Not a real workbook: stands in for the API's .xlsx, newest first.
      const rows = matching(pois, query.search)
        .reverse()
        .map((poi) =>
          [
            poi.name,
            poi.latitude,
            poi.longitude,
            poi.icon,
            poi.remarks ?? "",
          ].join(","),
        );
      return new Blob(
        [["name,lat,lng,icon,remarks", ...rows].join("\n") + "\n"],
        {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      );
    },
    async downloadTemplate() {
      await wait();
      // Not a real workbook: stands in for the API's .xlsx.
      return new Blob(["name,lat,lng,icon,remarks\n"], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
    },
    async previewBulkUpload(file) {
      await wait();
      const refused = unsupportedFile(file);
      if (refused !== undefined) throw refused;
      const rows = checkSheet(MOCK_BULK_SHEET, pois);
      const count = (status: PoiBulkRow["status"]) =>
        rows.filter((row) => row.status === status).length;
      return {
        totalRecords: rows.length,
        validRecords: count("VALID"),
        invalidRecords: count("INVALID"),
        duplicateRecords: count("DUPLICATE"),
        rows,
      };
    },
    async bulkUpload(file) {
      await wait();
      const refused = unsupportedFile(file);
      if (refused !== undefined) throw refused;
      const rows = checkSheet(MOCK_BULK_SHEET, pois);
      const now = new Date().toISOString();
      const failedRows = rows.filter((row) => row.status !== "VALID");
      for (const row of rows) {
        const icon = sheetIcon(row.icon);
        if (row.status !== "VALID" || icon === undefined) continue;
        next += 1;
        pois.push({
          id: `poi-${String(next)}`,
          name: row.name?.trim() ?? "",
          latitude: Number(row.latitude),
          longitude: Number(row.longitude),
          icon,
          remarks: row.remarks,
          accountId: "account-1",
          createdAt: now,
          updatedAt: now,
        });
      }
      const created = rows.length - failedRows.length;
      return {
        totalRecords: rows.length,
        createdRecords: created,
        failedRecords: failedRows.length,
        failedRows,
        message:
          failedRows.length === 0
            ? `Bulk upload completed successfully. ${String(created)} POIs were created.`
            : `${String(created)} POIs were successfully imported. ${String(failedRows.length)} records could not be imported.`,
      };
    },
    async downloadBulkErrorReport(file) {
      await wait();
      const refused = unsupportedFile(file);
      if (refused !== undefined) throw refused;
      // Not a real workbook: stands in for the API's .xlsx of refused rows.
      const refusedRows = checkSheet(MOCK_BULK_SHEET, pois)
        .filter((row) => row.status !== "VALID")
        .map((row) => `${String(row.row)},${row.errors.join(" / ")}`);
      return new Blob([["row,errors", ...refusedRows].join("\n")], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
    },
    async getById(id) {
      await wait();
      const poi = pois.find((kept) => kept.id === id);
      if (poi === undefined) throw notFound(id, "GET");
      return poi;
    },
    async delete(id) {
      await wait();
      const index = pois.findIndex((poi) => poi.id === id);
      if (index === -1) throw notFound(id, "DELETE");
      pois.splice(index, 1);
      return { message: "POI deleted successfully." };
    },
  };
}
